import React, { useEffect, useState } from 'react';
import {
    Drawer, Box, Typography, IconButton, Button, CircularProgress, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
    Alert, Divider, TextField, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ApiSettingsForm from '../api/ApiSettingsForm';

const FIELD_LABELS = {
    api_alarm_url: 'Alarms > URL',
    api_alarm_http_method: 'Alarms > HTTP Method',
    api_alarm_text_format: 'Alarms > Text Format',
    api_alarm_enabled: 'Alarms > Enabled',
    api_alarm_emails: 'Alarms > Emails',
    api_alarm_emails_enabled: 'Alarms > Emails Enabled',

    api_error_url: 'Errors > URL',
    api_error_http_method: 'Errors > HTTP Method',
    api_error_text_format: 'Errors > Text Format',
    api_error_enabled: 'Errors > Enabled',
    api_error_emails: 'Errors > Emails',
    api_error_emails_enabled: 'Errors > Emails Enabled',

    api_analyzer_url: 'Analyzers > URL',
    api_analyzer_http_method: 'Analyzers > HTTP Method',
    api_analyzer_text_format: 'Analyzers > Text Format',
    api_analyzer_enabled: 'Analyzers > Enabled',

    api_counter_url: 'Counters > URL',
    api_counter_http_method: 'Counters > HTTP Method',
    api_counter_text_format: 'Counters > Text Format',
    api_counter_enabled: 'Counters > Enabled',

    api_enhanced_analyzer_url: 'Enhanced > URL',
    api_enhanced_analyzer_http_method: 'Enhanced > HTTP Method',
    api_enhanced_analyzer_text_format: 'Enhanced > Text Format',
    api_enhanced_analyzer_enabled: 'Enhanced > Enabled',

    api_record_url: 'Record > URL',
    api_record_http_method: 'Record > HTTP Method',
    api_record_text_format: 'Record > Text Format',
    api_record_enabled: 'Record > Enabled',

    simple_alarm_conversion_action: 'Alarms > Conversion Action',
    simple_alarm_conversion_min_duration: 'Alarms > Minimum Duration',
    simple_alarm_conversion_alarm_frequency: 'Alarms > Alarm Frequency',
};

const DEFAULT_SIMPLE_ALARM_MIN_DURATION = 1;
const DEFAULT_SIMPLE_ALARM_FREQUENCY = 10;

const BulkApiDrawer = ({showSms}) => {
    const [open, setOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [submitUrl, setSubmitUrl] = useState('/groups/api-update');
    const [changes, setChanges] = useState({});
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            setGroupName(e.detail.group_name);
            setSubmitUrl(e.detail.url || '/groups/api-update');
            setOpen(true);
            setChanges({});
        };
        window.addEventListener('openBulkApiDrawer', handler);
        return () => window.removeEventListener('openBulkApiDrawer', handler);
    }, []);

    const handleClose = () => setOpen(false);

    const handleChange = ({ name, value }) => {
        setChanges((prev) => {
            if (name === 'simple_alarm_conversion_action') {
                if (value === 'promote') {
                    return {
                        ...prev,
                        simple_alarm_conversion_action: value,
                        simple_alarm_conversion_min_duration: prev.simple_alarm_conversion_min_duration ?? DEFAULT_SIMPLE_ALARM_MIN_DURATION,
                        simple_alarm_conversion_alarm_frequency: prev.simple_alarm_conversion_alarm_frequency ?? DEFAULT_SIMPLE_ALARM_FREQUENCY,
                    };
                }

                if (value === 'revert' || value === '') {
                    const next = { ...prev, simple_alarm_conversion_action: value };
                    delete next.simple_alarm_conversion_min_duration;
                    delete next.simple_alarm_conversion_alarm_frequency;
                    return next;
                }
            }

            return { ...prev, [name]: value };
        });
    };

    const handleSubmit = async () => {
        setConfirmOpen(false);
        setLoading(true);
        try {
            const response = await fetch(submitUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': window.csrfToken || document.querySelector('input[name="_token"]').value,
                },
                body: JSON.stringify({ group_name: groupName, updated: changes })
            });
            const result = await response.json();
            const details = [];
            if (result.total_updates !== undefined) {
                details.push(`Updated: ${result.total_updates} fields`);
            }
            if (result.alarm_conversion_updates !== undefined && result.alarm_conversion_action) {
                details.push(`Alarm conversions: ${result.alarm_conversion_updates}`);
            }
            if (result.restarted_fluxes) {
                details.push(`Restarted fluxes: ${result.restarted_fluxes}`);
            }
            if (Array.isArray(result.warnings) && result.warnings.length) {
                details.push(`Warnings: ${result.warnings.join(' | ')}`);
            }

            const extra = details.length ? `\n${details.join('\n')}` : '';
            setAlert({
                open: true,
                message: `${result.message || 'Update successful!'}${extra}`,
                severity: Array.isArray(result.warnings) && result.warnings.length ? 'warning' : 'success'
            });
            setOpen(false);
        } catch (err) {
            setAlert({ open: true, message: 'Update failed.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const openConfirmation = () => {
        if (!Object.keys(changes).length) return;
        setConfirmOpen(true);
    };

    return (
        <>
            <Drawer anchor="right" open={open} onClose={handleClose} sx={{ zIndex: 1300 }}>
                <Box sx={{ width: 900, p: 3, position: 'relative' }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" gutterBottom>
                        Bulk Group Update ({groupName})
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action will <strong>overwrite the selected settings</strong> for all items in this group. Values may differ between items and will not be displayed here. <br /><br />
                        <strong>Changes are irreversible</strong>, please review carefully before saving.
                    </Alert>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Alarm Conversion
                        </Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Editing or deleting any promoted alarm later clears the revert marker for the whole converted set. Bulk revert only works for items whose generated alarms stayed unchanged.
                        </Alert>
                        <TextField
                            select
                            fullWidth
                            label="Simple alarm conversion"
                            value={changes.simple_alarm_conversion_action || ''}
                            onChange={(event) => handleChange({ name: 'simple_alarm_conversion_action', value: event.target.value })}
                            disabled={loading}
                            sx={{ mb: 2 }}
                        >
                            <MenuItem value="">No change</MenuItem>
                            <MenuItem value="promote">Promote all simple alarms to Alarm Management</MenuItem>
                            <MenuItem value="revert">Revert unchanged converted simple alarms</MenuItem>
                        </TextField>
                        {changes.simple_alarm_conversion_action === 'promote' && (
                            <>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Minimum scenario duration (seconds)"
                                    inputProps={{ min: 1 }}
                                    value={changes.simple_alarm_conversion_min_duration ?? DEFAULT_SIMPLE_ALARM_MIN_DURATION}
                                    onChange={(event) => handleChange({ name: 'simple_alarm_conversion_min_duration', value: event.target.value })}
                                    disabled={loading}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Alarm frequency (seconds, optional)"
                                    inputProps={{ min: 0 }}
                                    value={changes.simple_alarm_conversion_alarm_frequency ?? DEFAULT_SIMPLE_ALARM_FREQUENCY}
                                    onChange={(event) => handleChange({ name: 'simple_alarm_conversion_alarm_frequency', value: event.target.value })}
                                    disabled={loading}
                                />
                            </>
                        )}
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Typography variant="subtitle1" gutterBottom>
                        API Settings
                    </Typography>
                    <ApiSettingsForm
                        item={{}}
                        disabled={loading}
                        showSms={showSms}
                        onChange={handleChange}
                        trackChanges
                    />

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                        <Button onClick={openConfirmation} variant="contained" disabled={loading || !Object.keys(changes).length}>
                            {loading ? <CircularProgress size={24} /> : 'Update'}
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Bulk Update</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        You are about to apply the following changes to <strong>{groupName}</strong>:
                    </Typography>
                    {changes.simple_alarm_conversion_action && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            If promoted alarms are edited or deleted later in Alarm Management, automatic revert will no longer be available for the rest of that converted set.
                        </Alert>
                    )}
                    <List dense>
                        {Object.entries(changes).map(([key, value]) => (
                            <ListItem key={key}>
                                <ListItemText
                                    primary={`${FIELD_LABELS[key] || key}: ${String(value)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Confirm & Update
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={5000}
                onClose={() => setAlert({ ...alert, open: false })}
                message={alert.message}
            />
        </>
    );
};

export default BulkApiDrawer;
